import { CommonModule } from '@angular/common';
import { Component, HostListener, Input, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { filter, Subscription } from 'rxjs';
import { AccountService } from '../../../../Utils/_services/account/account.service';
import { ThemeService } from '../../../../Utils/_services/theme/theme.service';

interface SidebarItem {
  label: string;
  icon?: string;
  route: string;
  isDisabled?: boolean;
}

@Component({
  selector: 'user-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.css'
})
export class SidebarComponent implements OnInit {

  //properties
  @Input() collapsed: boolean = false;
  currentRoute = '';
  role: string = 'User';
  sidebarItems: SidebarItem[] = [];
  private userSub?: Subscription;

  adminItems: SidebarItem[] = [
    { label: 'Dashboard', icon: 'bi-speedometer2', route: '/admin/dashboard' },
    { label: 'App Management', icon: 'bi-grid', route: '/admin/app-management' },
    { label: 'Join App Waitlist', icon: "bi-clipboard-check", route: "/admin/app-waitlist"},
    { label: 'Post Jobs', icon: 'bi-briefcase', route: '/admin/job-post' },
    { label: 'Applied Jobs', icon: 'fa-regular bi-clipboard', route: '/admin/applied-job' },
    { label: 'Manage Users', icon: 'bi-people', route: '/admin/users' },
    { label: 'Newsletter Blog', icon: 'bi-journal', route: '/admin/newsletters' },
    { label: 'Bug Reporting', icon: 'bi-bug', route: '/admin/bug-reporting' },
    { label: 'Feature Request', icon: 'bi-bug', route: '/admin/feature-request', isDisabled: true },
    { label: 'Beta Tester', icon: "fa-solid fa-users-between-lines", route: "/admin/beta-tester", isDisabled: true},
    { label: 'Blocked Accounts', icon: "fa-solid fa-hand", route: "/admin/blocked-accounts", isDisabled: true},
    { label: 'Banned Accounts', icon: "bi-slash-circle", route: "/admin/banned-accounts", isDisabled: true},
    { label: 'Brand Reconition', icon: "fa-solid fa-copyright", route: "/admin/brand-reconition", isDisabled: true},
    { label: 'Contact Us', icon: "fa-solid fa-copyright", route: "/admin/brand-reconition", isDisabled: true},
    { label: 'Logs', icon: "fa-solid fa-copyright", route: "/admin/logs"},
    { label: 'Feature Flag', icon: "fa-solid fa-copyright", route: "/admin/brand-reconition", isDisabled: true},
    { label: 'Terraform Log', icon: "fa-solid fa-copyright", route: "/admin/brand-reconition", isDisabled: true},
    { label: 'App Build History', icon: "fa-solid fa-copyright", route: "/admin/brand-reconition", isDisabled: true},
    { label: 'Video Downloader', icon: "fa-solid fa-copyright", route: "/admin/brand-reconition", isDisabled: true},
    { label: 'Clear Cache', icon: "fa-solid fa-copyright", route: "/admin/brand-reconition", isDisabled: true},
  ];

  userItems: SidebarItem[] = [
    { label: 'Dashboard', icon: 'bi-speedometer2', route: '/user/dashboard' },
    { label: 'Marketing', icon: 'bi-megaphone', route: '/user/marketing' },
    { label: 'Brand Images', icon: 'bi-image', route: '/user/brand-images' },
    { label: 'Two-Factor Auth', icon: 'bi-shield-lock', route: '/user/two-fa' },
    { label: 'Accounts', icon: 'bi-person-circle', route: '/user/accounts' },
    { label: 'KYC Verification', icon: 'bi-person-vcard', route: '/user/kyc' },
    { label: 'Contact Responses', icon: 'bi-envelope', route: '/user/contact-responses' },
    { label: 'Reported Bugs', icon: 'bi-bug', route: '/user/reported-bug' },
    { label: 'Request Features', icon: 'bi-lightbulb', route: '/user/request-features' },
    { label: 'Career Submission', icon: 'bi-briefcase', route: '/user/career-submission' },
    { label: 'Affiliate', icon: 'bi-diagram-3', route: '/user/affiliate' },
    { label: 'Billing', icon: 'bi-credit-card', route: '/user/billing' },
    { label: 'Partner', icon: 'bi-handshake', route: '/user/partner' },
    { label: 'Notifications', icon: 'bi-bell', route: '/user/notifications' }
  ];

  constructor(
    private router: Router, 
    private toastr: ToastrService,
    private accountService: AccountService,
    public themeService: ThemeService
  ) {
    // Listen to route changes
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.currentRoute = event.url;
    });

    // Initial route setup
    this.currentRoute = this.router.url;
  }

  ngOnInit(): void {
    this.setCollapsedBasedOnWidth();
    
    this.userSub = this.accountService.currentUser$.subscribe(user => {
      if (user) {
        this.role = (user.role?.toLowerCase() === 'admin' || user.role?.toLowerCase() === 'administrator') ? 'Admin' : 'User';
        this.sidebarItems = this.role === 'Admin' ? this.adminItems : this.userItems;
      } else {
        this.sidebarItems = this.userItems; // default fallback
      }
    });
  }

  ngOnDestroy(): void {
    this.userSub?.unsubscribe();
  }

  @HostListener('window:resize', [])
  onResize(): void {
    this.setCollapsedBasedOnWidth();
  }

  private setCollapsedBasedOnWidth(): void {
    this.collapsed = window.innerWidth <= 991;
  }

  navigate(item: SidebarItem): void {
    if (item.isDisabled) {
      this.toastr.warning(`${item.route} is not implemented yet`);
    } else {
      this.router.navigate([item.route]);
    }
  }

  isActive(route: string): boolean {
    return this.currentRoute === route;
  }
}
